/**
 * WORLD tuning. Every magic number the city is built from lives here.
 * ARCHITECTURE.md §4.11 — one frozen export, findable in one place.
 *
 * Units: metres, seconds, radians.
 *
 * COLOUR NOTE. §10 says palette anchors live in `src/materials/palette.js`, owned by `look`.
 * That file does not exist yet and `ctx.materials` is still core's grey fallback, so the
 * facade tints below are carried as *vertex-colour multipliers* on merged masonry meshes.
 * They are transcribed from `reference/palette/palette.md` (sampled from the actual Montreal
 * photographs), not invented. When `look` lands a palette these should migrate to it and this
 * table should become identity-ish. See docs/report-world.md.
 */

export const TUNING = Object.freeze({
  rngLabel: 'world',

  // ------------------------------------------------------------------ street grid
  grid: Object.freeze({
    /**
     * PHASE-1 EXPANSION. The city grew from 7×7 lines (~390 × 380 m) to 10×10 (~590 m each
     * way) by adding three rues to the WEST (−X) and three avenues to the SOUTH (+Z). −Z is
     * north and +X is east; see `roadAt`, which sends the east half of a rue toward −Z.
     *
     * ⚠ THE ORIGINAL SEVEN COORDINATES ARE UNCHANGED AND MUST STAY THAT WAY. Every graded
     * scenario, the pothole layout, the church vista and eleven rounds of facade tuning sit on
     * them. The new lines are additions, never edits — which also means the west additions had
     * to be PREPENDED (nsX must stay ascending for `blockAt`'s range test), so NS street
     * INDICES shift by 3. Nothing persists an index: `NS_NAMES` is index-matched and was
     * extended at the same end, and `spawnPoints().streetIndex` is only used by gameplay for
     * band balancing. See `src/world/layout-key.js` for how the city stays bit-identical under
     * an index shift like this one.
     *
     * Block pitch out west/south is deliberately looser than the Plateau's 58-70 m — downtown
     * Montreal blocks are bigger, and a bigger block is also a CHEAPER block here, because a
     * tower lot replaces ~20 triplex lots' worth of staircases and balconies.
     */
    /**
     * North-south streets ("rues"), by world X.
     *
     * PHASE 3 prepended three more at the west end for Mont Royal, so indices shift AGAIN by
     * three — the same non-event it was in phase 1, because `layout-key.js` keys every block,
     * lot and street-prop stream on world COORDINATE rather than index. Indices 0-2 are the
     * mountain, 3-5 the phase-1 downtown, 6-12 the original Plateau.
     *
     * −546 is the summit street: the mountain's peak sits exactly on its intersection with the
     * av. du Mont-Royal artère at z −62, so the highest point of the road network is a real
     * four-way junction and the belvédère can stand on its corner.
     */
    nsX: Object.freeze([-616, -546, -474,
      -396, -320, -250, -192, -126, -62, 0, 58, 124, 194]),
    nsWidth: Object.freeze([12.0, 13.5, 12.0,
      12.0, 20.5, 13.0, 12.0, 12.5, 19.0, 13.0, 12.0, 12.5, 12.0]),
    nsKind: Object.freeze(['street', 'street', 'street',
      'street', 'artere', 'street',
      'street', 'street', 'artere', 'street', 'street', 'street', 'street']),

    /** East-west streets ("avenues"), by world Z. Indices 7-9 are the downtown additions. */
    ewZ: Object.freeze([-188, -124, -62, 0, 64, 122, 188, 258, 330, 400]),
    ewWidth: Object.freeze([12.0, 12.0, 21.0, 12.5, 12.0, 12.5, 12.0, 13.0, 21.0, 12.0]),
    ewKind: Object.freeze(['street', 'street', 'artere', 'street', 'street', 'street',
      'street', 'street', 'artere', 'street']),

    sidewalkStreet: 3.1,
    sidewalkArtere: 4.5,
    /** Kerb face height. Low enough that an arcade truck can hop it. */
    kerbHeight: 0.145,
    /** Horizontal band around the kerb line reported as surface 'kerb'. */
    kerbBand: 0.22,
    /** Centre-line crown of the carriageway. Montreal streets are not flat. */
    roadCrown: 0.095,
    laneWidth: 3.3,
    /**
     * Rasterisation cell for the road/sidewalk surface mesh. 2.4 → 2.95.
     *
     * ROUND-11 §9. `roadway` + `sidewalk` + `kerbs` are 43,316 live triangles in `winter-slush`,
     * the shot that gates, and they are the only geometry in the frame whose cost scales with
     * the SQUARE of a single number. The carriageway's shape is a 95 mm centre crown over a
     * broad terrain swell — at the graded camera a 2.95 m cell samples that to well under a
     * pixel of error, and nothing else reads off this mesh (`roadY()` is analytic, so vehicle
     * physics, pothole placement and the patch quilt are all unaffected). 2.4² / 2.95² = 0.66,
     * i.e. about 14,600 triangles back for no visible change. Taken to 3.25 in the same round
     * when another owner's landing put `winter-slush` back over the gate: same argument, another
     * ~5,400.
     */
    /**
     * PHASE 3: 3.25 → 3.75. Same argument the round-11 note above makes, applied to a city
     * that is now 832 × 610 m instead of 408 × 398. `world:roadway` measured 39,528 triangles
     * in night-rain — the single largest mesh world owns, and one of the handful that is merged
     * globally and therefore never frustum-culled, so every frame pays for the carriageway
     * under Mont Royal whether or not it is on screen. 3.25² / 3.75² = 0.75, i.e. a quarter of
     * roadway + sidewalk + kerbs back. Nothing reads off this mesh — `roadY()` is analytic, so
     * physics, pothole placement and the patch quilt are unaffected — and at 3.75 m the 95 mm
     * centre crown is still sampled far below a pixel of error at any graded camera.
     */
    surfaceCell: 3.75,
  }),

  /**
   * Back alleys. At least one runs the whole city and is a real shortcut.
   *
   * Ruelle Napoléon's south end followed the grid out to z 400 in the phase-1 expansion so it
   * still runs the full length. That only touches blocks south of z 188, all of which are new,
   * so the old core's block subdivision is untouched. `Ruelle Saint-Antoine` is the downtown
   * one — a real service lane between rue Guy and rue Peel, which is what a tower block's back
   * of house looks like.
   */
  ruelles: Object.freeze([
    Object.freeze({ name: 'Ruelle Napoléon', axis: 'ns', pos: -29.0, from: -188, to: 400, width: 6.2 }),
    /**
     * −358, i.e. mid-block between av. Atwater and rue Guy. It was at −285 (between Guy and
     * Peel) and that block's interior is only 42.8 m wide, because the Guy artère eats 14.75 m
     * of it — an alley down the middle left two 13 m strips, `towerLots` rejected them, and the
     * THREE HIGHEST-SCORING DOWNTOWN BLOCKS silently fell back to being Plateau. Measured: 5
     * towers instead of 11. The Atwater-Guy column is 52 m of interior and splits into two
     * ~19 m parcels, which is a real downtown slab either side of a real loading lane.
     */
    Object.freeze({ name: 'Ruelle Saint-Antoine', axis: 'ns', pos: -358.0, from: 188, to: 400, width: 6.8 }),
    Object.freeze({ name: 'Ruelle Sainte-Cunégonde', axis: 'ns', pos: 91.0, from: -62, to: 122, width: 5.6 }),
    Object.freeze({ name: 'Ruelle des Bagels', axis: 'ew', pos: 32.0, from: -126, to: 124, width: 5.8 }),
  ]),

  // ------------------------------------------------------------------ terrain
  terrain: Object.freeze({
    /** Broad city undulation — amplitude, spatial frequency. */
    swellA: 0.85, swellFx: 0.0061, swellPx: 0.9,
    swellB: 0.75, swellFz: 0.0069, swellPz: -0.35,
    swellC: 0.55, swellFd: 0.0033,
    /** The jump. A gaussian crest on rue Napoléon, mid-block so it is not an intersection. */
    hill: Object.freeze({ x: 0, z: -92, sx: 34, sz: 13.5, h: 2.45 }),
    /**
     * MONT ROYAL — a real, drivable mountain on the west edge. `height()` is closed form, so
     * every consumer (surface raster, lane sampling, prop placement, collision, the perimeter
     * barrier) inherits the elevation for free; that claim is verified per subsystem rather
     * than assumed — see tools output in the phase-3 report.
     *
     * PEAK HEIGHT IS DERIVED FROM THE GRADE LIMIT, NOT PICKED. For a gaussian the steepest
     * slope is 0.858·h/σ, and every street here is axis-aligned, so that expression IS the
     * worst street grade. Solving for the 10-15 % arcade-climb band at σ = 190 m gives
     * h = 30 m → 13.5 %, measured numerically at 13.5 % across the whole footprint.
     *
     * σ is what protects the existing city. A 30 m rise at ≤13.5 % needs ~220 m of flank no
     * matter how it is shaped, so the summit has to sit that far west of anything that must
     * stay put. At σ = 190 from (−546, −62) the measured rise is 0.01 m at the origin, 0.84 m
     * at rue Drolet and 0.15 m under downtown — the graded shots are effectively untouched,
     * while the westernmost pre-phase-3 street column lifts ~16 m, which is exactly the real
     * Plateau tilting up toward the mountain.
     *
     * `shoulder` is a subsidiary summit (Westmount, in effect). It also replaces the painted
     * backdrop lobe that used to stand here — see landmarks.mountain.
     */
    montRoyal: Object.freeze({
      x: -546, z: -62, h: 30.0, sigma: 190,
      /**
       * The subsidiary summit. Its POSITION is a grade decision, not a taste one: at
       * (−616, −150) it sat on the main lobe's steep north flank and their gradients ADDED,
       * measured 19.35 % against a 15 % ceiling. Moved south to (−600, 190) its own slope
       * never lines up with the main one and the worst street grade is 13.99 %, which is what
       * the mountain measures with no shoulder at all (13.92 %).
       */
      shoulder: Object.freeze({ x: -600, z: 190, h: 9.0, sigma: 160 }),
    }),
    /** Finite-difference step for the ground normal. */
    normalEps: 0.75,
  }),

  // ------------------------------------------------------------------ blocks & lots
  block: Object.freeze({
    /** Footprint of the building that owns a block corner (fronts both streets). */
    cornerLen: 15.0,
    /** Chamfer across a corner building's outside corner — the dépanneur door. */
    cornerChamfer: 2.6,
    lotWidthMin: 9.4,
    lotWidthMax: 13.2,
    depthMin: 10.5,
    depthMax: 15.5,
    /** Gap kept between the back of a building and the ruelle / the opposite row. */
    yardMin: 2.2,
  }),

  // ------------------------------------------------------------------ Mont Royal park
  /**
   * The park district on the mountain. See src/world/mountain.js.
   *
   * `threshold` is in METRES OF MOUNTAIN, not a radius: a block is park when Mont Royal's own
   * contribution at its centre clears it. That makes the park a contour of the terrain, so it
   * moves with the summit and cannot drift out of register with the hill it is supposed to be
   * covering.
   */
  park: Object.freeze({
    threshold: 8.0,
    /**
     * Forest density. `canopy*` has NO far-LOD twin — unlike cones, balconies and windows —
     * so every live tree is ~157 triangles at full price and is charged again if it casts.
     * A 17 m lattice at p 0.62 puts roughly 14-18 trees on a 70 m block, which still reads as
     * woods from the road. It was 14 m; night-rain came back 35k over the 1.35M tripwire after
     * phase 3 and the forest was the largest single addition, so the lattice widened. Raise it
     * only with a measurement in hand.
     */
    treeStep: 17.0,
    treeP: 0.62,
    treeEdge: 5.0,
    treeScaleMin: 0.9,
    treeScaleMax: 1.5,

    /** The belvédère. Anchored to the summit and offset onto the junction's view corner. */
    lookout: Object.freeze({
      offX: 26, offZ: 24,
      width: 34, depth: 26,
      /** Deck sits this far above the summit carriageway, and skirts this far below it. */
      lift: 0.9,
      skirt: 9.0,
      deckUv: 0.22,
      parapetT: 0.7,
      parapetH: 1.05,
      furnitureIn: 1.8,
      lamps: 3,
      benches: 3,
    }),
  }),

  // ------------------------------------------------------------------ vista
  /**
   * Low-poly block shells for the far city, so the belvédère has something to overlook. See
   * mountain.js buildVistaShells. Ten triangles a block, mutually exclusive with sector detail.
   */
  vista: Object.freeze({
    /**
     * Camera altitude above which the far-city shells switch on. 18 m clears `wide-establish`'s
     * 15 m elevated camera — no graded shot gains a vista layer — and everything on Mont Royal
     * is well above it. See the note in WorldSystem.update().
     */
    minCamY: 18,
    inset: 2.2,
    drop: 0.8,
    skirt: 3.0,
    uv: 0.06,
  }),

  // ------------------------------------------------------------------ downtown
  /**
   * The south-west quadrant. See src/world/downtown.js for the zoning model and why a tower
   * block is CHEAPER than a Plateau block.
   *
   * `fadeR` is the load-bearing number: the nearest point of the pre-expansion city to the
   * anchor (the outside SW corner of the grid) is 294 m away, so ending the fade at 285
   * guarantees every graded shot scores exactly zero and cannot sprout a tower.
   */
  downtown: Object.freeze({
    /**
     * The zone's anchor, held explicitly rather than derived from the grid corner — see
     * downtownAnchor(). These are the phase-1 corridor's south-west corner.
     */
    anchorX: -405.1,
    anchorZ: 409.1,
    /** A block with more than this much Mont Royal under it is never a tower block. */
    maxMountain: 3.0,
    /** Distance from the SW anchor: full downtown inside coreR, pure Plateau beyond fadeR. */
    coreR: 170,
    fadeR: 285,
    /** Block score above which the block is re-lotted into podium parcels. */
    towerThreshold: 0.32,
    /** …and above which an ordinary Plateau block gets transitional extra storeys. */
    midriseThreshold: 0.10,
    /** Extra storeys at score 1 on a midrise (non-tower) lot, scaled by score. */
    midriseStoreys: 4,

    /** Podium footprint is the block inset by this; below lotMin the block stays Plateau. */
    podiumSetback: 1.4,
    /**
     * 15, not 22. A mid-block ruelle through a downtown block leaves ~19 m either side, and 22
     * rejected every alley block outright — which does not fail loudly, it just hands the block
     * back to the Plateau generator. 19 × 65 m is an ordinary downtown slab parcel.
     */
    lotMin: 15,
    /** Two parcels rather than one superblock, when the long axis has room. */
    splitMin: 62,
    splitP: 0.6,
    parcelGap: 5.0,
    /** Clear space kept either side of a ruelle running through a downtown block. */
    alleyMargin: 2.4,

    podiumStoreys: Object.freeze([2, 2, 3]),
    podiumStoreyH: 4.6,
    podiumUv: 0.16,
    corniceH: 0.85,
    corniceOut: 0.55,
    /** Recessed glazed lobby/retail band around the podium base. */
    baseGlassInset: 0.42,
    baseGlassY0: 0.35,
    baseGlassY1: 4.35,
    baseGlassUv: 0.075,

    storeyH: 3.85,
    minStoreys: 8,
    maxStoreys: 34,
    /** Height is score × range × [heightJitter, 1], so the core still gets variety. */
    heightJitter: 0.52,
    /**
     * `prism` maps uv in METRES × uvScale, i.e. curtain-wall tile repeats per metre. 0.130 is
     * one tile per 7.7 m ≈ two storeys, which is the density the material owner specified for
     * the map they authored. I had it at 0.062 (one tile per four storeys) from reading
     * towerRows=4 as four storeys per tile; the A/B night capture showed panes about twice the
     * size they should be, so the map's rows are half-storeys and the owner's number is right.
     */
    glassUv: 0.130,
    /**
     * Lit-density variants, weighted. `look` requires most towers on '.dim'/'.dark' and only
     * one or two on '.bright', because §10 makes the CONES the highest-contrast objects in
     * frame and a fully-lit downtown is the one thing that could take that away at night.
     * Weights over 11 towers give roughly 1-2 bright.
     */
    litVariants: Object.freeze(['Dark', 'Dim', 'Bright']),
    litWeights: Object.freeze([0.30, 0.58, 0.12]),
    /**
     * A tower this tall never draws '.dark', so the hero mass always has windows in it.
     *
     * Set to 24 while I still believed '.dark' was the cause of `look`'s "central tower stays
     * entirely black" report, which suppressed the variant completely (the 11 towers average
     * 24.3 storeys). It was not the cause — the mullion fins were, see buildTower() — so this
     * is back to 30, where a genuinely shorter tower can carry the shuttered look and read as
     * an empty building rather than as missing geometry.
     */
    litTallStoreys: 30,
    litVariantsTall: Object.freeze(['Dim', 'Bright']),
    litWeightsTall: Object.freeze([0.72, 0.28]),

    shaftInsetFrac: 0.17,
    shaftInsetMax: 7.0,
    /** Narrow, deep slab masses — the Place Ville Marie silhouette. */
    slabP: 0.38,
    slabFrac: 0.52,

    /**
     * Fins REMOVED — see the note in buildTower(). Kept as a record: these described geometry
     * that duplicated the curtain-wall map's own mullion line and, because the boxes spanned
     * the full shaft width, blacked out whole facades.
     */

    setbackP: 0.55,
    setbackMinStoreys: 16,
    setbackAt: 0.68,
    setbackIn: 0.70,
    bandH: 1.5,

    crownFrac: 0.62,
    crownH: 4.2,
    mastP: 0.4,
    mastH: 11.0,

    /**
     * FAR-SHELL INSET. 0.22 → 1.00, and a matching 1.0 m drop off the top.
     *
     * 0.22 m is inside depth-buffer precision at the 150-300 m downtown is viewed from, so the
     * dark shell won the depth test across most of every facade and hid the lit curtain wall
     * completely. `look` measured it with the visibility-toggle protocol (§4.9b): bright pixels
     * 1,311 with the shell → 36,514 without, a factor of 28. See buildSkylineShells().
     *
     * 1.0 m is far outside depth precision at any range the shell is visible from, and far
     * inside the silhouette it has to fill once the detailed sector is culled at 427 m — at
     * that distance a metre subtends well under a pixel.
     */
    shellInset: 1.00,
    shellTopDrop: 1.00,
  }),

  // ------------------------------------------------------------------ buildings
  building: Object.freeze({
    /**
     * How far below the lot's LOWEST corner the mass starts. Was a flat −2.2 m from mean
     * grade, which floats the downhill corner ~1 m on a Mont Royal fringe lot. See build().
     */
    baseBury: 2.2,
    storeyH: 3.16,
    storeyJitter: 0.14,
    /** Residential ground floor sits on a short stone base with a stoop. */
    baseH: 0.58,
    corniceH: 0.44,
    corniceOut: 0.30,
    parapetH: 0.56,
    parapetIn: 0.08,
    /** Odds of 2 vs 3 vs 4 storeys, by street kind. */
    storeyWeights: Object.freeze({
      street: Object.freeze([0.22, 0.62, 0.16]),
      artere: Object.freeze([0.06, 0.52, 0.42]),
      ruelle: Object.freeze([0.40, 0.55, 0.05]),
    }),
    /**
     * Ground-floor commercial probability by street kind.
     *
     * ROUND-8: corner 0.52 → 0.78. Illuminated signage is the subject of `night-rain`
     * criterion 8 and half of `wide-establish` criterion 6, and it can only be graded if a
     * shop is actually in the frame — `night-rain`'s scenario puts the truck on a SIDE street,
     * where `street: 0.08` means eight lots in a hundred carry a sign. The dépanneur du coin
     * is on the CORNER, which is also the lot least likely to be carrying a street-facing
     * exterior staircase, so this buys signage without spending the staircase rhythm that
     * `street-speed` criterion 8 and `wide-establish` criterion 4 both test. `street` is
     * deliberately left alone for exactly that reason.
     */
    commercialP: Object.freeze({ street: 0.08, artere: 0.92, corner: 0.78 }),
    /**
     * Masonry family weights [brick, stone, render], by lot kind.
     *
     * ROUND-8. `wide-establish` criterion 8 is not "greystone appears" — it is "greystone
     * appears ... CONCENTRATED ON A CORNER OR INSTITUTIONAL BUILDING RATHER THAN DISTRIBUTED
     * RANDOMLY". The old weights were [0.48,0.38,0.14] / [0.52,0.19,0.29] applied per lot with
     * no reference to position, which is the literal definition of distributed randomly, so
     * the criterion could only ever half-pass however good the tint was.
     *
     * It is also how the city was actually built: the greystone went up on the corner as the
     * bank, the pharmacie and the institution, and the mid-block infill is brick and painted
     * render. Corners now read greystone at ~0.72, mid-block streets at 0.08.
     */
    familyWeights: Object.freeze({
      corner: Object.freeze([0.16, 0.72, 0.12]),
      artere: Object.freeze([0.56, 0.18, 0.26]),
      street: Object.freeze([0.58, 0.08, 0.34]),
    }),
    /** Mansard roof with dormers instead of a flat parapet. */
    mansardP: 0.26,
    mansardRise: 2.7,
    mansardSetBack: 2.3,
    bayWindowP: 0.24,
    bayWidth: 2.5,
    bayOut: 0.72,

    windowW: 1.16,
    windowH: 1.78,
    /**
     * DEPTH OF THE PROJECTING WINDOW FRAME, metres — see world.js `windowGeo` for the geometry
     * and for why round 10's "recess it into the wall" version could not work against a solid
     * masonry prism. The pane sits ~0.86 of this behind the frame's front rim, so 0.12 buys a
     * ~0.10 m reveal: deep enough to throw a real shadow line under the head at a 39° sun and
     * comfortably inside `look`'s 0.22 m near AO sample radius.
     */
    windowReveal: 0.12,
    /** Pale stone lintel + sill over every opening. This is what reads as Montreal at 60 m. */
    lintelH: 0.17,
    /**
     * HOW FAR THE LINTEL / SILL BANDS EAT INTO THE WALL, metres.
     *
     * Round-10 `wide-establish`, verbatim: "Windows and sills are extruded slabs FLOATING IN
     * FRONT OF the wall plane with visible gaps". Measured on the round-10 build: the lintel
     * box ran from +0.005 m to +0.145 m and the sill from +0.0325 m to +0.2225 m, i.e. both
     * started OUTSIDE the masonry, and because they are emitted with the BAND face mask (front,
     * top, underside — no back, no ends) you could see into the gap behind them. They are now
     * placed by depth: back face at −`lintelEmbed`, so the band is INSIDE the wall it belongs to
     * and the open back is buried where nobody can look into it.
     */
    lintelEmbed: 0.03,
    /** Front-to-back thickness of the two bands. Projection = thickness − lintelEmbed. */
    lintelDepth: 0.20,
    sillDepth: 0.25,
    /**
     * STONE JAMBS. Width across the facade and depth out of it, for the two vertical members
     * that turn a lintel-and-sill pair into a closed surround. Deeper than the lintel on
     * purpose: the jamb has to return past the sash before the eye reads a reveal.
     */
    jambW: 0.20,
    jambDepth: 0.24,
    /** Belt course at each floor line — see buildings.js for the flat-facade gap it closes. */
    courseH: 0.15,
    courseDepth: 0.16,
    /**
     * ⚠ MINIMUM CLEAR HEIGHT FROM THE SAMPLED GROUND TO A WINDOW SILL, metres. Below this the
     * bay is not built at all — see buildings.js `_windowRow` for the round-11 fatal this
     * closes (a complete window with its sill rendering on the sidewalk plane with no wall
     * around it, because placement was measured off the lot's MEAN ground height).
     */
    minSillAboveGround: 0.72,
    lintelOver: 0.19,
    baySpacing: 2.72,
    bayMin: 2,
    bayMax: 3,
    /**
     * WALL-FOOT AO. Height of the darkening gusset where masonry meets pavement, its outward
     * offset (so it does not z-fight the wall), and the tint multiplier at the ground. See
     * MeshBuilder.skirt(). 1.0 at the top means it fades into the wall's own colour.
     */
    footAoH: 1.35,
    footAoOut: 0.022,
    footAoDark: 0.58,
    /** The hard contact line: how tall, and how dark at the pavement itself. */
    footAoContactH: 0.22,
    footAoContact: 0.26,
    /**
     * TEXTURE REPEATS PER METRE on a wall, by masonry family.
     *
     * Round-10 `wide-establish`: "The right quarter of the frame is one undifferentiated tiled
     * stone mass." At 400% the greystone courses measured roughly 1.5 m across — a real
     * Montréal greystone block is 0.5-0.8 m — so one texture tile was spanning nearly 3 m of
     * wall and the repeat was the only thing the eye could find. Brick was already fine and is
     * left alone; only the two big flat families are re-scaled.
     */
    wallUv: Object.freeze({ brick: 0.35, stone: 0.78, render: 0.52 }),

    storefrontH: 2.95,
    /**
     * DEPTH OF THE SHOPFRONT RECESS, metres. 0.26 → 0.78.
     *
     * ROUND-11 FATAL, and both `wide-establish` critics led with it: *"Below the cornice band
     * the ground storey is a featureless pale-grey plane. No wall, no glazing, no door, no
     * threshold. The sidewalk runs straight into a void. Street level — the only part a player
     * ever gets close to — is unbuilt on every hero building."*
     *
     * Measured, not inferred (§4.9b): raycasting the shot camera through the dépanneur's
     * glazing at (457,783) on `harness/out-final/wide-establish.png` returns
     * `masonry.glass` at x −12.2, then `sidewalk` at x −14.6 — i.e. the pavement, 2.4 m
     * FURTHER IN, with no wall between them. Forcing `world:masonry.stone` to BackSide fills
     * the storey in, so the recessed ground-floor prism's street face exists in the buffer and
     * is not being rasterised. Rather than keep the ground storey hanging on that one prism,
     * the shopfront now builds its OWN opaque assembly — piers, interior, stallriser, door,
     * threshold — which cannot be silently culled and is what the critic actually asked for.
     *
     * 0.78 also buys the recess a visible soffit and two jamb returns, so the shopfront reads
     * as something set INTO the mass rather than painted onto it.
     */
    storefrontRecess: 0.78,
    /** How far behind the glazing the shop's interior back wall sits. Parallax = depth. */
    shopDepth: 1.15,
    /** Unlit shop interior. Deliberately dark: a lit box behind glass reads as a hole. */
    shopBackTint: 0x2A2521,
    shopShelfTint: 0x6B5F4E,
    /** Masonry pier at each end of the shopfront — the jamb of the ground-floor opening. */
    pierW: 0.46,
    /** Door leaf, and the stone step that lands it on the pavement. */
    doorW: 1.05,
    doorH: 2.18,
    doorTint: 0x3A2C22,
    thresholdOut: 0.34,
    bulkheadH: 0.52,
    /**
     * ROUND-7: 0.98 → 1.26. `wide-establish` item 6 (French signage) failed outright — "every
     * awning and shopfront is a blank coloured quad ... sized so they resolve at this camera
     * distance, roughly 14-20 px cap height at the near block". At the shot's pose (64 m,
     * 40° vertical fov, 1080 px) one metre subtends 1080 / (2·64·tan20°) ≈ 23 px, and the
     * fascia's 0.78 m text band put the CAP height at ~0.42 m ≈ 10 px. 1.26 m of board carries
     * a 1.10 m band and a ~0.62 m cap — 14 px, i.e. the bottom of the range the critic asked
     * for, without inventing a signage system the atlas does not already draw.
     */
    fasciaH: 1.26,
    fasciaOut: 0.20,
    awningP: 0.34,
    awningOut: 1.55,
    awningDrop: 0.55,
    /**
     * AWNING BRACKETS. Round-10 `wide-establish`: "A black awning hangs unsupported." It did —
     * a tilted slab attached to nothing. Two diagonal stays now run from the fascia down to the
     * awning's outer corners, plus a front rail across the drop edge, which is how a fixed
     * shop awning on Saint-Denis is actually built. ~54 triangles per awning, into the merged
     * trim mesh, so zero draw calls.
     */
    awningStayR: 0.05,
    awningInset: 0.28,
    bladeSignP: 0.36,
    bladeOut: 1.45,
    bladeW: 1.30,
    bladeH: 1.14,
  }),

  // ------------------------------------------------------------------ the signature
  stairs: Object.freeze({
    /** Probability a residential lot gets an exterior staircase to the 1st-floor balcony. */
    p: 0.84,
    treadRun: 0.315,
    treadThick: 0.055,
    width: 1.02,
    /** Handrail height above the tread nosing. */
    railH: 0.93,
    railThick: 0.055,
    balusterEvery: 0.56,
    balusterThick: 0.028,
    stringerThick: 0.07,
    stringerDepth: 0.26,
    /** Radius of the quarter-turn at the foot of a curved stair. */
    curveR: 1.58,
    curveTreads: 4,
    spiralR: 1.30,
    spiralSweep: 4.6,        // radians
    /** Distance the stair sits out from the facade plane. */
    standOff: 0.22,
    /** Painted metal. Montreal stairs are black, forest green, oxide red, grey, or acid green. */
    paints: Object.freeze([
      0x24262a, 0x1d3026, 0x8e3a2a, 0x4a4d52, 0x2b2f36,
      0x24262a, 0x6f8f3a, 0x7c2a24, 0x30343a, 0x1d3026,
    ]),
  }),

  balcony: Object.freeze({
    deckH: 0.15,
    out: 1.58,
    railH: 0.98,
    railThick: 0.05,
    balusterEvery: 0.33,
    balusterThick: 0.024,
    /** Second-floor balcony probability. */
    upperP: 0.38,
  }),

  // ------------------------------------------------------------------ props
  props: Object.freeze({
    parkedSpacing: 6.3,
    /**
     * 0.74 → 0.66 in round 4. Giving ~650 on-screen parked cars real wheels and arches costs
     * triangles, and the city was already at 99 % of the 900k budget (§9); a slightly gappier
     * kerb is a cheaper price than cars that read as boxes. Still bumper-to-bumper by eye.
     */
    /**
     * LEAD INTEGRATION PASS (I6). Was 0.66 — world had cut it from 0.74 in round 4 to buy
     * triangles, and then ARCHITECTURE §3.1 retired traffic's 601-car field on top of that,
     * roughly HALVING the kerb population. street-speed's right kerb went from a continuous
     * line of metal to mostly empty sidewalk, which is REFERENCE §6.9 ("empty kerbs read as
     * a film set") — the exact criterion the round-2 critics failed us on.
     *
     * MEASURED like-for-like on the `street-speed` capture (my first pass compared capture
     * numbers against a PROFILER baseline, which is a lighter scene — that was wrong):
     *     parkedP 0.66 -> 924,269 tris   (already over the 900k budget, before any change)
     *     parkedP 0.85 -> 951,019 tris   (+26,750, i.e. +2.9%)
     *     parkedP 0.92 -> 957,197 tris   (+32,928)
     * So parked cars are NOT the triangle problem — the graded camera simply sees more of
     * the city than the profiler's scenario does. Density costs 2.9% and buys back a
     * criterion we have failed since round 2, so it is worth taking.
     *
     * `world`: the overage is pre-existing and lives elsewhere. A cheaper FAR-LOD car body
     * would still buy back the most headroom here if you want more density later.
     */
    parkedP: 0.85,
    parkedOffset: 1.35,          // from kerb line into the carriageway
    parkedClearIntersection: 11.0,
    treeSpacing: 12.8,
    treeP: 0.55,
    treeOffset: 1.35,            // from kerb line into the sidewalk
    poleSpacingStreet: 30.0,
    poleSpacingRuelle: 22.0,
    poleH: 9.4,
    wireSag: 0.9,
    /** Catenary tessellation. 6 → 4 in round 4: the sag still reads and it is a 33 % saving. */
    wireSegments: 4,
    /**
     * WIRE GAUGE, metres, and its colour. 0.035 → 0.055: at the `wide-establish` pose one
     * metre subtends ~23 px, so a 70 mm wire was 1.6 px and aliased to nothing even before the
     * cutout material (see world.js) discarded most of it. 110 mm holds ~2.5 px, which is what
     * `look` needs to resolve it as a continuous line.
     */
    wireR: 0.055,
    wireTint: 0x1C1A19,
    /** Wires per span. A Plateau pole carries a bundle, not a single line. */
    wireCount: 3,
    coneClusterEvery: 21.0,
    coneClusterP: 0.74,
    coneClusterMin: 2,
    coneClusterMax: 9,
    coneH: 0.72,
    coneBase: 0.36,

    /**
     * THE LANE-CLOSURE CORRIDOR (round 7).
     *
     * `cone-slalom` scored 4.5 with the fatal "there is no cone slalom in panel B — about five
     * cones exist, the tallest roughly 8 px by 5 px, all clustered at the vanishing point".
     * Every cone `world` owned was hugging a KERB (coneCluster above lays at
     * `half - 0.5 - rand(0,2.4)`), so from a chase camera down the middle of the carriageway
     * they are 5-6 m off-axis and shrink to nothing by the second block.
     *
     * A Montréal lane closure does not sit on the kerb — it walks IN off the kerb, runs a
     * corridor down the lane line, and walks back out. That is what the shot needed and what
     * rubric criterion 7 ("conspicuously excessive, overshooting the work zone at both ends")
     * describes. Laid on the LANE LINE, a corridor is directly in front of the chase camera at
     * every distance, which is the only place a cone can be large in frame.
     *
     * Cones are NOT colliders (see Props._collide), so this costs the player nothing.
     */
    coneTaperEvery: 58.0,
    coneTaperP: 0.68,
    /** Cones per corridor, before the lead-in taper. */
    coneTaperCount: 13,
    coneTaperStride: 3.1,
    /** Lateral half-offset of the corridor: cones alternate side of the lane line. */
    coneTaperLat: 2.35,
    coneTaperLatJitter: 0.45,
    /** Cones in the lead-in that walks from the kerb across to the corridor. */
    coneTaperLeadIn: 4,
    /** Fraction of corridor cones that are knocked flat (criterion 6 wants ≥1 per 8). */
    coneTaperKnockedP: 0.11,
    hydrantEvery: 58.0,
    binEvery: 41.0,
    bikeRackEvery: 74.0,
    fencePanel: 2.4,
    clotheslineP: 0.34,

    // ---------------------------------------------------------------- winter dress
    /**
     * ROUND-4 CORRECTION. Two fatals in `winter-slush`: summer canopies in a snow shot, and
     * snow that existed only as a ground decal. Everything below is the winter layer; it is
     * gated on `ctx.look.snowCover` (look owns the weather state, §N10) and is invisible in
     * every other weather.
     */
    /** ctx.look.snowCover above this ⇒ the city wears its winter dress. */
    snowGate: 0.35,
    /** Kerb-line ploughed banks. The critic's band is 0.6–1.1 m tall — land INSIDE it. */
    snowbankSeg: 6.5,
    snowbankH: 0.82,             // nominal crown height, m
    snowbankHJitter: 0.16,       // ±, so the run lands 0.66–0.98 m before ground slope
    snowbankWidth: 1.55,         // across the kerb line, m
    snowbankOverlap: 1.06,       // length multiplier on snowbankSeg → a continuous run
    /**
     * Lateral offset of the bank CENTRE from the kerb line, into the sidewalk. The ploughed
     * face then lands at half − 0.42 and the back at half + 1.06, which clears the parked-car
     * flank (half − 0.45) instead of intersecting it — a real ploughed bank sits ON the kerb.
     */
    snowbankLat: 0.28,
    /** The dirt-stained skirt at the foot of the bank (road splash). */
    snowbankDirtH: 0.34,
    /** Thickness of snow lying on a horizontal surface (car roofs, bins, copings). */
    snowCapT: 0.070,
    /** Snow cap on the tip of a cone, as a fraction of cone height. */
    coneCapR: 0.085,
    /** Snow lying on a building coping / cornice. */
    corniceCapT: 0.085,
    /**
     * Bare winter trees. Montreal is leafless from November to May; a full summer canopy in a
     * snow shot was the single worst read in round 3.
     */
    branchCount: 7,
    branchSub: 2,
    branchR: 0.075,
    branchLen: 2.35,
    branchRise: 0.62,
    /** Snow load sits on the UPPER surface of the main limbs only. */
    branchSnowW: 0.20,
    /**
     * One canopy MESH per leaf tint (see props.createSets) — three is the sweet spot between
     * "flat single-value green blobs" (round-3 critic) and the §9 draw-call budget, because
     * each set is drawn twice: once for the frame and once for the shadow map.
     */
    treeCanopy: Object.freeze([0x4f7528, 0x5c8330, 0x3d5c1e]),
    /** Front-yard hedge. Own tint because it now shares the solid `ground.grass` key. */
    hedgeTint: 0x44631f,
    /**
     * Parked-car paint. `look` publishes `traffic.paint.<i>` as exact-albedo tint VARIANTS of
     * one program (round-3 note: instanceColor is inert unless the material opts into
     * vertexColors, which costs a program — so per-instance colour goes through these keys).
     * Indices into palette.js TRAFFIC.paints: silver, white, charcoal, dark red, navy,
     * muted green, bronze, gunmetal.
     */
    /**
     * ROUND-7 NOTE, and a change that was MADE AND THEN REVERTED — record it so nobody
     * repeats the experiment.
     *
     * The `cone-slalom` critic logged a major: "an untextured, unlit, fully saturated red quad
     * clips through the road in the lower-left reading corner — the most saturated object in
     * the frame and an obvious rendering bug". A raycast through that pixel identifies it:
     * `world:car0_3`, material `traffic.paint|t8e1f1f` — a PARKED CAR's door flank, 4.4 m of
     * one flat colour filling the corner at 7.7 m. It measures `#900006`, i.e. the authored
     * `0x8E1F1F` has its GREEN CHANNEL CRUSHED TO ZERO somewhere in `traffic.paint`'s albedo
     * normalisation (library.js `_applyToOne` divides the tint by the map's per-channel mean).
     *
     * Swapping index 3 → 4 (navy) removes the offending hue, and it also turns `winter-slush`
     * — a protected round-6 WIN at 6.2 — into a monotone blue street, because `traffic` is
     * already blue-heavy. Not worth it. The flatness half of the complaint is fixed instead,
     * in carTrimGeo(): the flank now carries a rubbing strip. The saturation half is look's,
     * and is in the report.
     */
    carPaintKeys: Object.freeze([0, 1, 2, 3]),
    /** Weighted draw over carPaintKeys INDICES — Montreal is mostly silver/white/charcoal. */
    carPaintPick: Object.freeze([0, 0, 0, 1, 1, 1, 2, 2, 3]),
    /**
     * WHEEL RIM and TAIL LAMP, as cached tint variants (§4.9e — `get(key,{tint})`, never
     * `instanceOf`). The rim value has to clear the tyre by enough to survive a 4× zoom from
     * 8 m, which is the round-11 target: `traffic.tyre` sits near 0x1A1A1C.
     *
     * NOT on `truck.metal`. That key is `METAL.chrome` at high metalness, and a metal with no
     * strong environment behind it renders DARK — at 4× on `winter-slush` the first attempt
     * read as a thin hexagonal outline inside a black hole, not as a rim. `building.render` is
     * an albedo-driven non-metal that world already requests for poles and fences, so the tint
     * variant costs one draw call and zero programs and the rim keeps its value under the
     * overcast winter sky as well as under the thaw sun.
     */
    carRimTint: 0xC6CACD,
    carLampTint: 0xB8221C,
    /** palette.js FLORA.bareBranch — winter limb colour. */
    bareBranch: 0x453c33,
    /** palette.js FLORA.barkLight — the trunk was rendering as pale stucco. */
    trunkBark: 0x6e6558,
    /**
     * Road-splash stain at the foot of a kerbside bank, between palette.js SNOW.dirtyGrey
     * (0xA3A099) and SNOW.dirty (0x8D715C). Pure dirtyGrey was invisible against fresh snow.
     */
    snowDirt: 0x96877a,
    laundry: Object.freeze([0xf5f2ea, 0x7ab6dc, 0xe06a62, 0xf5cf52, 0xffffff, 0x9ccc86, 0xdcdce4, 0xe8a0c0]),
  }),

  // ------------------------------------------------------------------ facade tints
  /**
   * Vertex-colour multipliers applied to merged masonry. Transcribed from
   * reference/palette/palette.md. The single most important note there: unpainted Montreal
   * brick is a warm greyed brown, NOT saturated red. Only painted brick goes vivid.
   */
  tints: Object.freeze({
    brick: Object.freeze([
      // Straight from reference §6.3 / palette.md §3. Every one of these is inside the
      // #A38C74–#DBAA90 family the `wide-establish` rubric item 7 tests for.
      0xa38c74, 0xb9aa99, 0xc19173, 0xdbaa90, 0xbe9c68, 0xe2c8a7, 0xa8907a, 0xcf9f80,
      /**
       * ROUND-7. These six were "the warmer half again — under a cool key light the greyed
       * browns read as concrete", and three of them (`0xB8724F`, `0xA8624C`, `0xD08A62`) were
       * invented rather than sampled. Measured HSV saturation 0.57 / 0.55 / 0.53 against
       * item 7's `S ≤ 0.40` cap, and hue 18-22° where the reference family sits at 24-30°.
       * That is exactly the round-6 major: "brick albedo is outside the Montreal range on both
       * hue and saturation … reads orange terracotta rather than the buff/salmon family".
       * Re-cut to S 0.34-0.38, hue held: still the warm end of the block, inside the cap.
       */
      0xc19173, 0xdbaa90, 0xcf9f80, 0xbb8f74, 0xa8806a, 0xd0a184,
    ]),
    stone: Object.freeze([0xa7a193, 0xc8c1b6, 0xdcdad2, 0x928977, 0xb5afa2, 0xc2bcb0]),
    render: Object.freeze([0xc9493f, 0x2f5f7a, 0x6f8f3a, 0xd8b845, 0xdcdad2, 0x9c5f3e, 0x992932, 0x3d6b5a, 0xe0dcd0, 0xb85a28, 0xa8543f, 0xd8b845]),
    trim: Object.freeze([0xdcdad2, 0xd0cec6, 0xe6e2d8]),
    roof: Object.freeze([0x6e675b, 0x5f5a50, 0x7a7365, 0x554f46]),
    /** How far a facade tint may wander per lot (multiplicative luminance jitter). */
    jitter: 0.085,
    /** Exact albedos for the meshes that used to carry a vertex-colour clone (§9 programs). */
    outerGround: 0x4f6b2e,
    /**
     * ROUND-8: darkened from [0x3F5C26, 0x4F7528, 0x37501F]. The ridge now stands at 420-540 m
     * where `look`'s linear thaw fog (near 145 / far 620) still leaves 17-42 % of the albedo on
     * screen, so the authored value finally matters. A wooded mountain seen through that much
     * haze has to be authored WELL below the sky or it comes back out of the fog lighter than
     * the sky it is standing in front of — which is precisely the round-8 defect (measured
     * dome Ylin 0.5253 vs sky 0.4334).
     */
    /**
     * ROUND-11: *"Mont-Royal is a translucent ghost — a flat wedge with a razor-straight edge,
     * ~4 luma from the sky."* Darkened a further 0.72× on top of round 8's darkening. The mesh
     * is world's and the aerial perspective is `look`'s, so this is the half I own: at 420-540 m
     * `look`'s linear thaw fog (near 145 / far 620) leaves 17-42 % of the albedo on screen, so
     * the only lever on the near flank's contrast is how dark the albedo underneath is. FOR
     * `look`: if 4 luma is still the measured separation after this, the remaining half is the
     * fog curve, not the albedo — there is nothing darker than this that still reads as woodland.
     */
    mountain: Object.freeze([0x223310, 0x2a3f18, 0x1b280f]),
    crossWhite: 0xf0efe6,
    /**
     * The Croix du Mont-Royal is a steel lattice. By day, from the Plateau, it reads as a DARK
     * silhouette against the sky — not as a white bar. Painted white it was also the one object
     * in the frame whose fog-washed value matched the sky exactly, so it disappeared.
     */
    crossSteel: 0x33383c,
    /** reference §7.2 tar seam, `#2A2827`. Sits under every cured patch, 0.16 m proud of it. */
    /**
     * reference §7.2 authors the seam at `#2A2827`. Through `road.patch.cured`'s albedo
     * normalisation that renders as a near-black band, and at the `pothole-fill` camera the
     * seams around a dense quilt merge into a black web across the near road — which destroys
     * §3.2's "the road is the brightest large surface in frame". Authored lighter so the
     * RENDERED seam lands near the reference value instead of the authored one.
     */
    patchSeam: 0x4a4642,
    /** reference §7.3 "weeks to a season old" — palette.js PATCH.season1. */
    patchMid: 0x57585d,

    /**
     * ROUND-8 — THE KERB LINE. `combo-hud` major, and the same defect is what flattens
     * `wide-establish` and `street-speed`: "road and sidewalk are the same value, so the kerb
     * line disappears and the ground plane reads as one undifferentiated grey field across the
     * bottom 60 % of frame". MEASURED on the round-8 capture at wide-establish (900,700) road
     * `#A2A4A4` against (1250,690) sidewalk `#9B9D9E` — seven levels, i.e. nothing.
     *
     * The cause is the library default: `road.sidewalk` ships SIDEWALK.concrete `#8F8F8D`,
     * which is DARKER than `road.asphalt`'s `#A0A0A1`. REFERENCE §3.2 is explicit that Crazy
     * Taxi inverts the photographic relationship — "sidewalks and plazas are #C4C6C1-#CFD1CC,
     * only slightly brighter than the road, so the road/sidewalk boundary reads as a LINE (the
     * kerb) rather than a value step". `#C4C6C1` is palette.js SIDEWALK.paver, sampled from
     * ct-t552; this is the reference value, not an invention.
     *
     * Requested through `get(key,{tint})` (§4.9e), so the sidewalk still receives every
     * weather/night update and the winter snow-map swap. One cached variant, zero programs.
     */
    sidewalk: 0xc4c6c1,
    /**
     * …and the kerb FACE goes the other way. The face is the only part of the kerb the camera
     * sees from the carriageway, and §3.2 wants the boundary to read as a drawn line: a dark
     * band between a light sidewalk and a mid road does that at any distance and at any speed.
     * palette.js KERB.shade is `#6E6B66`; held a touch above it so the line does not read as a
     * gap in the ground plane at the `pothole-fill` camera distance.
     */
    kerbFace: 0x78756e,
    /**
     * The perimeter jersey barrier. Precast highway concrete is paler and greyer than a cast
     * kerb face and much paler than the road, which is the whole point: at the map edge the
     * barrier has to separate from the asphalt in VALUE (§10 readability) from as far away as
     * the fog lets you see it, so the player reads "closed" before they arrive rather than
     * after they bounce. Its own tint variant of `road.curb` — cached and shared, so it still
     * takes every weather and night update (§4.9e) and costs zero shader programs.
     */
    boundaryConcrete: 0xb0aea6,
    /** Far-city vista shells. A shade the fog can take without going flat — see §10. */
    vistaShell: 0x8d8378,
    /**
     * Used ONLY if `look` has not registered the curtain-wall keys — see
     * downtown.js `towerMaterials()`. A missing key returns MaterialLibrary's debug magenta,
     * which would be a whole quadrant of it, so the quadrant falls back to plain
     * `building.stone` at roughly the right values instead.
     */
    towerSpandrelFallback: 0x4c5460,
    towerPodiumFallback: 0x9a958c,
  }),

  // ------------------------------------------------------------------ landmarks
  landmarks: Object.freeze({
    church: Object.freeze({
      x: 0, z: -214, naveW: 19, naveD: 30, naveH: 15.5,
      towerW: 8.4, towerH: 27, spireH: 17, crossH: 2.8, rot: 0,
    }),
    /**
     * MONT-ROYAL. ROUND-8 REBUILD — the old version was the ugliest object in `wide-establish`.
     *
     * It was ONE ellipsoid, r 320 / h 112, centred at (-395,-625): 771 m from the graded
     * camera. `look`'s thaw fog is LINEAR near 145 / far 620, so at 771 m the mountain was
     * **100 % fog colour** — no albedo of mine could ever reach the screen. Fog colour is
     * pulled 0.72 toward the sky's HORIZON band, which is lighter than the sky overhead, and
     * the dome's summit sat high in frame against that darker sky. MEASURED on the round-8
     * capture: dome (400,180) Ylin 0.5253 against sky (960,190) Ylin 0.4334 — the "mountain"
     * was 21 % BRIGHTER than the sky behind it, with a hard elliptical rim. It read as a
     * soap bubble, not as a mountain, and three rounds of tint work could not have fixed it.
     *
     * Two changes, both geometric because the colour was never reachable:
     *
     *  1. A RIDGE, not a dome. Six overlapping lobes of different height and radius, so the
     *     skyline edge is broken instead of being one circular arc.
     *  2. It runs TOWARD the camera. The near flank sits ~420 m out (fog ≈ 0.58, so more than
     *     40 % of its own albedo survives) and the summit ~540 m (fog ≈ 0.83). Aerial
     *     perspective is then a real gradient ACROSS the object, which is what a distant
     *     wooded mountain actually looks like — and it is the only way to get any hue at all
     *     out of geometry standing beyond a 620 m fog plane.
     *
     * Footprint constraint: no lobe ellipse may reach z > -245, so nothing can intersect the
     * block grid (outermost carriageway edge ≈ z -197). Verified per lobe below.
     */
    mountain: Object.freeze({
      /** Summit — the cross stands here. */
      x: -300, z: -620, h: 55, rings: 4, segs: 18,
      /** Lobes are sunk this far below local ground so no rim edge is ever visible. */
      sink: 9,
      /**
       * dx/dz from the summit, ellipse radii, height as a fraction of `h`, and which of the
       * three hill tints (dark / mid / darkest) the lobe is built on.
       */
      /**
       * PHASE 3: KEPT, BUT DEMOTED — moved north and lowered, and it no longer carries the
       * Croix.
       *
       * I removed it outright first, on the reasoning that a real drivable mountain makes a
       * painted one redundant. Looking at the resulting `wide-establish` proved that wrong: the
       * real summit is due WEST and that camera faces north-east, so deleting this left open
       * sky where a mountain had been and took the Mont-Royal silhouette — a §10 non-negotiable
       * identifier — out of the establishing shot entirely.
       *
       * So it stays as the massif's distant NORTHERN SHOULDER, which is also how the real
       * mountain works (three summits running north-east toward Outremont). z −400 → −620 and
       * h 84 → 55: at its old position and height an 84 m painted dome stood 200 m north of a
       * 30 m real one and dwarfed it. From the belvédère it is now ~580 m out and fogged; from
       * the old core it still fills the horizon it used to.
       *
       * The Croix moved to the REAL summit, where the player can drive to it.
       */
      ridge: Object.freeze([
        Object.freeze({ dx: 0, dz: 0, rx: 190, rz: 152, hf: 1.00, t: 1 }),
        Object.freeze({ dx: 148, dz: 40, rx: 132, rz: 108, hf: 0.54, t: 0 }),
        Object.freeze({ dx: -172, dz: -74, rx: 158, rz: 126, hf: 0.79, t: 2 }),
        Object.freeze({ dx: -352, dz: 10, rx: 146, rz: 120, hf: 0.46, t: 0 }),
        Object.freeze({ dx: 62, dz: -196, rx: 168, rz: 138, hf: 0.66, t: 2 }),
        Object.freeze({ dx: -206, dz: -260, rx: 184, rz: 150, hf: 0.52, t: 1 }),
        Object.freeze({ dx: 78, dz: 56, rx: 104, rz: 96, hf: 0.71, t: 2 }),
        Object.freeze({ dx: -96, dz: 44, rx: 112, rz: 92, hf: 0.88, t: 0 }),
      ]),
    }),
    cross: Object.freeze({ h: 26.0, armY: 17.5, armW: 11.0, bar: 1.5 }),
    /** The Croix now stands on the REAL summit, offset clear of the belvédère deck. */
    crossOffX: -18,
    crossOffZ: -20,
    /**
     * Downtown on the far horizon. ROUND-4: was 9 flat untextured slabs with hard vertical
     * edges — the critic read them as pale-blue cards, because at 620 m the fog wash left one
     * value across the whole silhouette. Now each tower is a stepped mass with a crown and
     * horizontal glazing bands, and the mesh is split into three depth tiers so aerial
     * perspective is baked into the ALBEDO rather than left entirely to fog.
     */
    /**
     * ROUND-8 — WHY THE TIERS DID NOTHING, AND THE ONE NUMBER THAT FIXES IT.
     *
     * Rounds 4, 5 and 7 all added silhouette and albedo work to this skyline and a critic
     * still logged "flat untextured slabs with hard vertical edges and no aerial perspective".
     * The work was fine; it was never being drawn. `look`'s thaw fog is LINEAR with
     * near = 145 m and far = 620 m, and the three depth tiers sat at z 500 / 620 / 750, i.e.
     * fog factors of 0.75 / 1.00 / 1.00 from a camera near the origin. **Two of the three tiers
     * were 100 % fog colour** — literally flat cards — and the third was 75 % of the way there,
     * so `tierTint` and `bandTint` could not produce a value difference no matter what they
     * were set to.
     *
     * The tiers now sit at 430 / 535 / 640 → fog 0.60 / 0.82 / 1.00. That is a genuine
     * near-to-far ramp with the far tier still dissolving into haze, which is what aerial
     * perspective looks like. Heights drop 18 % so the horizon silhouette subtends about what
     * it did before and `street-speed` (a protected 7.5 win) does not gain a skyline it never
     * had — it gains DEPTH in the one it already had.
     */
    skyline: Object.freeze({
      /**
       * PHASE-1 MOVE: z 620 → 800, x 120 → −160, spread 260 → 320.
       *
       * Measured after the grid expansion: the near tier sat at z 430 against a south boundary
       * at z 411 — NINETEEN METRES outside the barrier. A backdrop authored to be read through
       * 400 m of fog was about to be standing on the pavement. It now sits 199 m clear of the
       * boundary and the x span was swung west to sit BEHIND the real downtown quadrant, so it
       * reads as the city continuing past the map edge rather than as a separate object off to
       * one side.
       *
       * It is fully fogged from the old core now (tiers 610/715/820 against `look`'s 620 m far
       * plane), which is the condition the round-8 note calls a failure. That judgement was
       * made when this WAS the skyline; it no longer is. Real towers at 190-410 m now carry the
       * near and mid skyline, and this carries the haze behind them — which is the job aerial
       * perspective actually wants it to do. From downtown itself the tiers are 200-410 m out,
       * i.e. squarely inside the fog ramp the tier tints were authored against.
       */
      x: -160, z: 800, spread: 320, count: 13, hMin: 38, hMax: 97,
      /**
       * Storey band pitch on a tower face: one dark glazing band per this many metres.
       * ROUND-5: 4.2 → 5.6. At 500–750 m a 4.2 m pitch is under two pixels, so the bands
       * resolved into an even grey wash and the tower read flat again — which is exactly what
       * the round-4 critic still saw after the round-4 rewrite. A coarser pitch with a taller,
       * darker band is the only version of this that survives the distance.
       */
      bandPitch: 5.6,
      /** Glazing band height as a fraction of the pitch. */
      bandFrac: 0.54,
      /** How far the band is inset from the wall plane (it is a recess, not a decal). */
      bandInset: 0.35,
      /** Chance a tower gets a setback shoulder partway up, and the setback fraction. */
      setbackP: 0.62,
      setbackAt: 0.66,
      setbackIn: 0.72,
      /** Crown: mechanical box + mast. */
      crownH: 4.6,
      mastP: 0.45,
      mastH: 9.0,
      /** Three depth tiers, near→far. Tier k sits at z + tierZ[k] and uses tierTint[k]. */
      tierZ: Object.freeze([-190, -85, 20]),
      /**
       * Aerial perspective, transcribed from reference/palette.md sky/haze behaviour: the
       * further tier is lighter and bluer, closer to the fog colour. Applied as an exact
       * albedo through ctx.materials.get('building.stone', {tint}) — zero extra programs.
       */
      /**
       * ROUND-8: near tier pulled DOWN (was 0x6F7C8C). Now that the near tier is only 60 %
       * fogged its albedo actually reaches the screen, so it can carry the dark end of the
       * ramp instead of being pre-lightened to compensate for a wash it no longer gets.
       */
      tierTint: Object.freeze([0x5f6b7a, 0x828f9d, 0xa1aebb]),
      /**
       * The recessed glazing band is a value step DOWN from the wall of the same tier.
       * ROUND-5: each band tint dropped ~14 luma. Fog at 620 m lifts everything toward the sky
       * colour and compresses the wall/band gap to nothing; the gap has to be authored wide
       * enough to survive that, not wide enough to look right unfogged.
       */
      bandTint: Object.freeze([0x2d3742, 0x4c5865, 0x6f7c8b]),
      /** ROUND-7: fraction of towers built as a narrow, deep SLAB rather than a fat block. */
      slabP: 0.34,
      /** Full-height service-core mullion: width as a fraction of the shaft, and its offset. */
      mullionFrac: 0.10,
      mullionAt: 0.21,
    }),
    /**
     * Raised 760 → 1000 with the skyline. The far tier now stands at z 820 and the ground has
     * to reach past it or the towers float over nothing. `outerGroundRing` (boundary.js) tiles
     * whatever extent it is given as a ring around `graph.bounds`, so this is the only number
     * that needed to change; measured cost is about +1,850 triangles, all of it beyond the
     * perimeter barrier and therefore never closer to the camera than the barrier is.
     */
    groundExtent: 1000,
  }),

  // ------------------------------------------------------------------ level of detail
  /**
   * ROUND-5. The round-4 build measured **1,533,161 triangles against a 900k budget** (§9) —
   * 70 % over. The overrun is not "too many props"; their absence was itself a critic fatal.
   * It is repeated micro-geometry — balusters, window reveals, fence pickets, cone bands —
   * that is sub-pixel past ~60 m and still costs full price, twice, because most of it also
   * casts into the shadow map.
   *
   * The fix is a NEAR/FAR twin per repeated prop (geom.js LodPair) plus a real contact shadow
   * so the far twin does not need to cast at all. Nothing is deleted: at the detail radius the
   * two geometries occupy the same silhouette and the swap is invisible.
   */
  lod: Object.freeze({
    /** Blocks whose centre is inside this of the camera get full-detail geometry, metres. */
    nearRadius: 74,
    /** Hysteresis band: a near block stays near out to here, so the boundary cannot chatter. */
    farRadius: 104,
    /**
     * Floor on the interval between LOD-only re-packs, in fixed steps. Re-packing the LOD
     * instance buffers is the one genuinely expensive thing this subsystem does per frame, and
     * at 74 m nobody can see the swap arrive half a second late. A change in the ACTIVE block
     * set is never rate-limited — that one is visible.
     */
    minRepackSteps: 30,
    /**
     * The presentation-side LOD re-check only fires when the camera has JUMPED this far since
     * the last evaluation — i.e. when the harness has detached the chase cam for a shot. During
     * play the streaming tick (every stream.everySteps) owns LOD, because re-packing 80 instance
     * buffers on a frame the boundary happens to cross costs ~2 ms and 15 MB of transient heap;
     * doing it at 6 Hz instead of 60 Hz measured p99 10.4 → 8.9 and heapGrowth 23.2 → 8.4.
     */
    recheckMove: 30.0,
    /** Coarse staircase: chord the helix rail every N treads instead of every tread. */
    stairRailChord: 9,
    /** Coarse staircase: baluster spacing multiplier. */
    stairBalusterSkip: 8,
    /**
     * Coarse balcony: baluster spacing multiplier on the street-facing rail, and the return
     * rails lose their pickets entirely.
     *
     * NOT a solid infill panel — that was tried first and it turned every distant balcony into
     * a black bar, which is the exact defect ("they read as floating slabs", "every exterior
     * staircase and balcony is black") the round-4 critic already logged. A balcony has to stay
     * SEE-THROUGH at every distance; only the picket count is negotiable.
     */
    balconyBalusterSkip: 7,
    /** Coarse fence panel: pickets collapse to one cutout-textured plane this thick. */
    fenceInfillThick: 0.05,
    /** Far snowbank: cross-section rings. The near bank uses the full profile. */
    snowbankSegsFar: 1,
  }),

  /**
   * CONTACT SHADOWS. reference §3.4 asks for a HARD, near-black blob where an object meets the
   * pavement, DISTINCT from the broad soft cast shadow. Round-4 measurement: the darkest patch
   * under any vehicle was 0.793 of the lit road, against the reference's 0.107 — nothing in the
   * frame was pinned to the ground.
   *
   * `world` bakes its own rather than calling `ctx.fx.contactShadow()` per prop: there are
   * ~5,000 static props, they never move, and one InstancedMesh of 2-triangle quads on a
   * cached `fx.decal` tint variant costs ONE draw call, ZERO shader programs and ~10k triangles
   * — against the ~450k the shadow map was spending on the same props.
   */
  contact: Object.freeze({
    /** reference §3.4 contact-blob colour. */
    tint: 0x222823,
    /** Blob alpha at the core. The decal texture falls off to zero at its own rim. */
    opacity: 0.86,
    /** Height above the ground plane. polygonOffset on `fx.decal` does the rest. */
    lift: 0.015,
    /**
     * ROUND-7. THE BLOBS WERE THERE AND NOBODY COULD SEE THEM.
     *
     * Four blind critics reported "nothing in the scene casts a sun shadow" and measured a
     * cone base at 1.013× the road beside it — i.e. literally no darkening. The blobs were
     * not missing: `harnessStats().contactShadows` was 3,666 and a debug capture with the
     * contact set forced to flat red showed one under every cone, pole, tree and parked car.
     * They were invisible for the SECOND half of the ARCHITECTURE §4.8b trap, the half about
     * geometry rather than blending:
     *
     *   a blob centred under the object is occluded by that object.
     *
     * A 0.68 m circle centred on a cone whose own base square is 0.36 m across leaves a
     * ~0.16 m crescent, most of which is the cone's own dark side. Measured contribution of
     * the WHOLE static contact set to `street-speed`: 7,116 pixels, 0.34 % of frame.
     *
     * §3.4 says it plainly — "a simplified blob roughly matching the footprint, OFFSET BEHIND
     * AND TO ONE SIDE". So the blob now runs down-sun from the foot instead of hiding under it.
     */
    /** Sun azimuth/elevation this bake assumes. Mirrors src/render/tuning.js light.sun.thaw. */
    sunAzimuthDeg: 128,
    sunElevationDeg: 28,
    /**
     * Fraction of the geometric shadow run (h / tan(elevation)) actually drawn. A full-length
     * run at 28° turns a 9 m pole into a 17 m stripe, which is a cast shadow, not the graphic
     * anchor §3.4 describes — and `look` owns the real shadow map. This is the anchor.
     */
    /**
     * ROUND-12 — THE BLOBS WERE THERE, AND THEY HAD WALKED OFF THE PROPS.
     *
     * `winter-slush` critic: *"Cones, the jersey barrier base, the red barriers and the tree
     * trunk base have ZERO ground darkening — not a soft AO, not a decal, nothing."* Filed as a
     * missing REGISTRATION (world's props not being in a query `fx` auto-drives). Measured
     * before changing anything, by hiding `world:contact` and differencing the frame:
     *
     *     world:contact, whole winter-slush frame : 80,367 px (3.88%), mean darkening 13.9%
     *     …at a tree-trunk base                   : 0 px
     *     …at a barricade base                    : 92 of 16,500 px
     *     …at three cone bases                    : 842 / 3,148 / 8,401 px, patchy
     *
     * So the set is registered, instanced and drawing HARD — harder than `fx.contact`'s 3.12%
     * — and there is still nothing under the props. The offset is why. `runScale` 0.55 with a
     * 2.3 m cap put a TREE's blob 1.15 m down-sun of its trunk as a 3.45 × 5.75 m ellipse, so
     * the trunk stood at the very edge of its own shadow; and `spread` 1.5 then diluted what
     * was left over half again the footprint. Round 7 moved the blob off the foot to stop the
     * prop occluding it (§3.4, and that was right); it moved it too far.
     *
     * ⚠ SHORTENING THE RUN WAS TRIED AND IS WRONG — DO NOT REPEAT IT. `_blobRun` builds the
     * quad `sl + run` long, so the run is not only an OFFSET, it is most of the blob's length.
     * Taking runScale/runMax/spread to 0.26 / 1.05 / 1.22 and re-measuring the same regions
     * made every one of them WORSE, because it shrank the blob instead of moving it:
     *
     *                        before          after
     *     whole frame        80,367 px 13.9%  40,927 px 10.5%
     *     trunk base            299 px  9.2%      105 px  6.6%
     *     barricade base         92 px 18.1%       39 px 16.3%
     *     cone base mid-left    842 px 10.8%      456 px  9.9%
     *
     * Reverted to the shipped values. The blobs are not mis-placed; they are DILUTE, and the
     * lever that actually reaches that is the winter alpha below, not the geometry.
     */
    runScale: 0.55,
    /** Hard cap on the run, metres. */
    runMax: 2.3,
    /** Footprint multiplier, so the blob still covers the foot once it has been offset. */
    spread: 1.5,
    /** Derived: unit ground vector the shadow runs along, and the matching yaw for trs(). */
    dirYaw: (128 + 180) * Math.PI / 180,
    dirX: Math.sin((128 + 180) * Math.PI / 180),
    dirZ: Math.cos((128 + 180) * Math.PI / 180),
    /** Derived: metres of run per metre of prop height, before runScale/runMax. */
    runPerMetre: 1 / Math.tan(28 * Math.PI / 180),
    /**
     * Overcast has no key light, so winter gets a short, soft pooling instead of a directional
     * anchor — see reference §8 and the round-6 `winter-slush` win we must not regress.
     *
     * ROUND-12: 0.40 → 0.58. This is the half of the `winter-slush` "zero ground darkening"
     * complaint that is genuinely reachable. Measured peak darkening under a cone at 0.40 was
     * 44 %, and §4.8b's linear-space blend means alpha bites harder than sRGB intuition
     * suggests, so this is a real step and not a nudge. Still well short of the thaw 0.86,
     * because an overcast sky has no key light and the round-6 `winter-slush` win was partly
     * that soft pooling — this deepens it, it does not turn it into a cast shadow.
     */
    winterOpacity: 0.58,
    /**
     * Footprints, metres. `r` = round blob radius, or `w`/`l` for a rectangular one (l runs
     * along the prop's local +Z). `h` is the prop's height and drives the shadow run.
     * Anything not listed here gets no blob.
     */
    foot: Object.freeze({
      pole: Object.freeze({ r: 0.62, h: 9.0 }),
      post: Object.freeze({ r: 0.42, h: 2.75 }),
      signal: Object.freeze({ r: 0.52, h: 6.2 }),
      lampO: Object.freeze({ r: 0.66, h: 4.5 }),
      lampC: Object.freeze({ r: 0.62, h: 7.4 }),
      hydrant: Object.freeze({ r: 0.52, h: 0.8 }),
      bin: Object.freeze({ w: 1.05, l: 1.15, h: 1.0 }),
      bench: Object.freeze({ w: 0.95, l: 2.05, h: 0.95 }),
      planter: Object.freeze({ w: 1.35, l: 1.35, h: 0.75 }),
      skip: Object.freeze({ w: 2.5, l: 4.6, h: 1.3 }),
      rack: Object.freeze({ w: 0.9, l: 2.3, h: 0.8 }),
      bixi: Object.freeze({ w: 0.9, l: 2.3, h: 1.4 }),
      /** A street tree's shadow is the CANOPY's, not the trunk's — hence the wide footprint. */
      trunk: Object.freeze({ r: 1.15, h: 5.0 }),
      hedge: Object.freeze({ w: 1.5, l: 1.1, h: 1.1 }),
      barrel: Object.freeze({ r: 0.48, h: 0.95 }),
      barricade: Object.freeze({ w: 2.3, l: 0.8, h: 0.95 }),
      cone: Object.freeze({ r: 0.34, h: 0.72 }),
      garage: Object.freeze({ w: 6.4, l: 6.4, h: 2.7 }),
    }),
    /** Parked cars are placed through their own path; footprint per CAR kind. */
    car: Object.freeze([
      Object.freeze({ w: 2.15, l: 4.6, h: 1.5 }),
      Object.freeze({ w: 2.05, l: 4.0, h: 1.45 }),
      Object.freeze({ w: 2.25, l: 4.9, h: 1.75 }),
    ]),
  }),

  // ------------------------------------------------------------------ streaming
  stream: Object.freeze({
    /** Blocks within this of the player keep their props. */
    playerRadius: 250,
    /** …and within this of the camera, so a detached harness camera stays populated. */
    cameraRadius: 190,
    /**
     * Sector groups (2×2 blocks) beyond this from the camera are hidden entirely.
     *
     * 460 → 330 with the SECTOR_DIM change. The two numbers are a pair: at 460 plus a sector's
     * own radius the cull limit was 604 m, which on a 612 m city means nothing was ever culled
     * and the whole grid's masonry drew every frame. Smaller sectors only pay off if the radius
     * is tight enough to actually reject some of them. 330 + a 2×2 sector's ~97 m radius is a
     * 427 m limit, where `look`'s 620 m linear fog has already taken about 80 % of anything
     * that drops out.
     */
    sectorRadius: 330,
    /**
     * Sector `trim` (cornices, copings, lintels, sills, stoops, fascias, awnings) stops casting
     * into the shadow map beyond this from the camera. See WorldSystem.update() for the
     * measurement: those nine meshes are 84.6k triangles live and 169k effective, the largest
     * single item in world's share of the §9 budget. 96 m covers the near half of the visible
     * street in every graded shot, which is the only place a cornice's own shadow band resolves.
     *
     * 34 m, not 96: at 96 the sector pitch (127 m) plus a sector's own 46 m radius meant seven
     * of nine sectors still qualified from any interior camera and the measured saving was only
     * 18k. 34 keeps the one sector the player is standing in — where a cornice shadow is metres
     * wide — and drops the rest.
     */
    trimShadowRadius: 26,
    /**
     * Multiplier on both radii for the `establish` / `result` scenarios, which want to see far
     * more of the city than a chase camera does. Replaces the old force-everything switch —
     * see the note in WorldSystem._restream for the measurement that killed it.
     */
    establishScale: 1.5,
    /**
     * p99 CONTROLS — see the block comment in WorldSystem.update(). A newly visible merged mesh
     * is a first-time GPU upload; these three keep those uploads out of the middle of a drive.
     */
    warmFrames: 8,
    /** The last N warm frames hide the sectors and show the shells instead — see update(). */
    warmShellFrames: 4,
    instanceUploadSkip: true,
    /**
     * 55 → 12, and the stagger 1 → 4. Measured: at 55 m of hysteresis and one flip per frame
     * the visible sector set stays inflated, and the drive profile's p50 went 1.55 → 3.6-5.0 ms
     * and p95 7.5 → 13.2-14.6 while the p99 tail did NOT improve. Holding sectors on to avoid
     * an upload costs more than the upload did. 12 m is just enough to stop a camera parked on
     * a boundary flipping one every frame.
     */
    /**
     * BOTH NEUTRAL, AND THAT IS A MEASURED RESULT, NOT AN OVERSIGHT.
     *
     * Holding sectors on to avoid an upload costs more than the upload does. At 55 m of
     * hysteresis with one flip per frame the drive profile's p50 went 1.55 → 3.6-5.0 ms and
     * p95 7.5 → 13.2-14.6 while the p99 tail did not improve at all; at 12 m / 4 flips the
     * median was still inflated. The visible sector set is simply the dominant cost, and
     * anything that enlarges it loses more than it saves. Left in place, at zero, because the
     * knobs are the obvious thing for the next person to reach for and the numbers above are
     * why they should not.
     */
    sectorHysteresis: 0,
    sectorFlipsPerFrame: 999,
    /** Re-evaluate the active set every N fixed steps. */
    everySteps: 10,
  }),

  // ------------------------------------------------------------------ playfield edge
  /**
   * The perimeter jersey barrier. See src/world/boundary.js for why the colliders are
   * half-spaces rather than strips, and for the ground hole this shipped alongside.
   *
   * Profile is a stylised Ontario/Québec jersey barrier: 0.62 m at the foot, flaring in to a
   * 0.30 m coping at 0.98 m. Only the inner two facets, the coping and a flat back are emitted
   * — nothing can ever stand outside the wall to see the outer profile — so a slab is eight
   * triangles and the whole 1.6 km perimeter is about 2.2k.
   */
  boundary: Object.freeze({
    /** Centreline, metres INBOARD of the corridor edge: standing on the outermost kerb lip. */
    inset: 0.16,
    baseW: 0.62,
    kneeW: 0.40, kneeH: 0.26,
    topW: 0.30,
    /**
     * Total slab height. `embed` of it is buried, so the visible run is height − embed = 0.98 m
     * above the sidewalk plane. The barrier straddles the corridor edge, and the outer ground
     * beyond it sits a kerb-height (0.145 m) LOWER than the sidewalk — bury deeper than that
     * step or the outboard foot floats and you can see daylight under the wall.
     */
    height: 1.28,
    embed: 0.30,
    segment: 6.0,
    /** Seeded settle. Height steps read as a silhouette break; lean steps as a kinked run. */
    heightJitter: 0.035,
    leanJitter: 0.030,
    /**
     * One works barricade every N slabs (18 m), plus a cone beside it, offset so the four
     * walls do not start in phase. Both reuse existing PropSets, so they cost zero draw calls
     * and stream by block; the orange is what turns a grey wall into a closed street.
     */
    barricadeEvery: 3,
    barricadePhase: 2,
    /**
     * Both stand clear of the wall rather than against it. At 0.55 m the barricade's 0.95 m
     * A-frame was occluded to the waist by the 0.98 m coping from any driving-height camera,
     * so all the orange you got was a stripe floating on top of a grey wall. Out on the
     * pavement it reads as a whole object standing in front of a barrier, which is the picture.
     */
    barricadeOut: 1.15,
    barricadeTint: 0xf26a1b,
    /** Cone sits by the kerb line, a little further along, so the pair does not read as a row. */
    coneOut: 2.40,
    coneAlong: 2.2,
    /**
     * Collider band, as CLEARANCES either side of the barrier feet the run actually emitted —
     * not absolute heights. Perimeter terrain runs −0.9 m to +1.9 m and the ground is nowhere
     * near y = 0 (§4.9c), so an absolute pair would have been a guess. 9 m of headroom is far
     * more air than the truck can get anywhere near the edge; the only ramp is mid-map.
     */
    colliderDepth: 6,
    colliderTop: 9,
  }),

  // ------------------------------------------------------------------ collision
  collision: Object.freeze({
    cell: 9.0,
    /** Max contacts returned by one collide() call. */
    maxContacts: 12,
    /** Poles / trees / hydrants are cylinders, buildings and cars are boxes. */
    poleR: 0.19,
    treeR: 0.28,
    hydrantR: 0.22,
  }),

  // ------------------------------------------------------------------ spawn points
  spawn: Object.freeze({
    potholeEvery: 17.0,
    potholeEdgeBias: 0.62,
    trafficEvery: 26.0,
    pedEvery: 9.0,
    coneEvery: 13.0,
    /** Keep spawns this far clear of an intersection centre. */
    intersectionClear: 12.0,
  }),

  /** Sign atlas resolution. Everything procedural, drawn on a canvas. */
  signs: Object.freeze({
    /**
     * ROUND-8 — THE SIGNAGE FIX THAT BLOCKED `look` FOR THREE ROUNDS (ARCHITECTURE §4.9e).
     *
     * Both signage materials were `materials.instanceOf()` PRIVATE CLONES with
     * `emissiveIntensity` pinned to 1.0, so by contract they never received `applyLook` and
     * `MAT_TUNING.mat.signEmissive` could not physically reach them. `night-rain` criterion 8
     * ("illuminated signage has a near-white emissive core with the colour in the halo") has
     * failed every round and `look` had already spent its half of the fix (bloom threshold
     * 0.72 -> 0.62, strength 0.86 -> 1.00) on a material that could not respond.
     *
     * Both are now `get(key, {map, tint})` — CACHED, SHARED, look-driven. `alphaTest` and
     * `emissiveMap` are not expressible through the opts bag, so they are applied once at
     * build time by `_adoptSignMaterial()`; see the note there for why that is safe.
     *
     * Second half of the same defect: the only geometry on the emissive material was the
     * ~1.9 m lit window lettering, DEEP INSIDE the storefront recess. Every sign a critic
     * actually named — the POUTINE blade, the dépanneur fascia — was on the flat panel
     * material. Fascias and blade signs are backlit boxes in real life and are now on the lit
     * material; `ARRÊT`, parking panels, roadworks boards and street blades stay flat, because
     * those are retroreflective, not illuminated.
     */
    panelAlphaTest: 0.40,
    litAlphaTest: 0.35,
    atlas: 2048,
    fasciaW: 512, fasciaH: 104,
    bladeW: 160, bladeH: 232,
    plateW: 168, plateH: 168,
    parkW: 136, parkH: 300,
    streetW: 300, streetH: 68,
  }),
});

export default TUNING;
